/* eslint-disable react/prop-types */
import {
  Typography,
  useTheme,
  Box,
  Grid,
  TextField,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { AlertModal, CustomDataGrid, DialogBox } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { strings } from 'i18n/Strings';
import { commonConstantTexts } from 'constants/constantText';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import {
  setCurrentPage,
  setSortOrder,
  setSortBy,
} from 'store/component/pagination';
import {
  viewTransferOutRequests,
} from 'service/RequestsService';
import { type RootState } from 'store/rootReducer';
import { type PaginationValue, type PageType } from 'store/component/pagination';
import Buttons from 'components/elements/Buttons';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import {
  getEmployeeDetailsByEmailOrUniqueId,
  transferOutUserUnits,
} from 'service/OrderService';
import { type SearchUserDataType } from 'pages/Orders/TodayOrder';
import { zpUserUniqueId } from 'utils/Constants';

interface Request extends GridValidRowModel {
  requestId: string;
  requestedDate: string;
  updatedAt: string;
  name: string;
  email: string;
  uniqueId: string;
  userId: number;
}

interface TransferOutResponse {
  items: Request[];
  total: number;
  page: number;
  size: number;
}

interface Filters {
  startDate: string | null;
  endDate: string | null;
}

const TransferOutRequests = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [transferData, setTransferData] = useState<Request[]>([]);
  const dispatch = useDispatch();
  const [pageCount, setPageCount] = useState<number | undefined>(undefined);
  const pageInfo = useSelector(
    (state: RootState) => state?.pagination?.transferOutRequests,
  ) as PaginationValue | undefined;
  const [filters, setFilters] = useState<Filters>({
    startDate: null,
    endDate: null,
  });
  const [filterSearchValue, setFilterSearchValue] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchedUserData, setSearchedUserData] =
    useState<SearchUserDataType | null>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [transferOutValues, setTransferOutValues] = useState<any>({
    fund1: { id: 1, value: '' },
    fund2: { id: 2, value: '' },
    fund3: { id: 3, value: '' },
    fund4: { id: 4, value: '' },
    fund5: { id: 5, value: '' },
    fund6: { id: 6, value: '' },
  });

  const getAllTransferOutRequests = async (): Promise<void> => {
    try {
      const transferRequests = (await viewTransferOutRequests(
        Number(pageInfo?.page ?? 0) + 1,
        pageInfo?.pageSize ?? 10,
        pageInfo?.sortBy ?? '',
        pageInfo?.sortOrder ?? '',
        filterSearchValue,
        filters,
      )) as TransferOutResponse;
      setTransferData(transferRequests?.items ?? []);
      setPageCount(transferRequests?.total ?? 0);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getAllTransferOutRequests();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    filterSearchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleFilterChange = (filterType: keyof Filters, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setFilterSearchValue(event.target.value);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleApplyFilter = () => {
    dispatch(
      setCurrentPage({
        key: commonConstantTexts.transferOutRequestPage as PageType,
        value: 0,
      }),
    );

    void getAllTransferOutRequests();
  };

  const handleTransferOutClick = (): void => {
    setOpenDialog(true);
  };

  const handleDialogClose = (): void => {
    setOpenDialog(false);
    setSearchValue('');
    setSearchedUserData(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setTransferOutValues({
      fund1: { id: 1, value: '' },
      fund2: { id: 2, value: '' },
      fund3: { id: 3, value: '' },
      fund4: { id: 4, value: '' },
      fund5: { id: 5, value: '' },
      fund6: { id: 6, value: '' },
    });
  };

  const handleUserSearch = async (value: string): Promise<void> => {
    try {
      const searchResult = await getEmployeeDetailsByEmailOrUniqueId(value);
      if (searchResult) {
        setSearchedUserData(searchResult);
        dispatch(
          setCurrentPage({
            key: commonConstantTexts.transferOutRequestPage as PageType,
            value: 0,
          }),
        );
      } else {
        setSearchedUserData(null);
        dispatch(
          setSnackBarFailed({
            snackBarMessage: strings.displayText.noUserDataFound,
          }),
        );
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fundId: number,
    maxValue: number,
  ) => {
    const value = e.target.value;
    if (!isNaN(Number(value)) && +value <= maxValue) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setTransferOutValues((prevValues: any) => ({
        ...prevValues,
        [`fund${fundId}`]: {
          id: fundId,
          value,
        },
      }));
    }
  };

  const handleTransferOut = async (): Promise<void> => {
    transferOutUserUnits(searchedUserData?.uniqueId, transferOutValues)
      .then(async () => {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.transferOutSuccessfully,
          }),
        );
        handleDialogClose();
        setOpenConfirmationDialog(false);
        await getAllTransferOutRequests();
      })
      .catch((error) => {
        setOpenConfirmationDialog(false);
        if (searchValue === '') {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: strings.displayText.customerIdRequiredText,
            }),
          );
        } else {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: error as string,
            }),
          );
        }
      });
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'requestId',
      width: 120,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.planChangeRequestId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.requestId}
        </Typography>
      ),
    },
    {
      field: 'requestedDate',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.requestedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.requestedDate)}
        </Typography>
      ),
    },
    {
      field: 'name',
      width: 180,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.name}
        </Typography>
      ),
    },
    {
      field: 'email',
      width: 260,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.email}
        </Typography>
      ),
    },
    {
      field: 'uniqueId',
      width: 220,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.uniqueId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.uniqueId}
        </Typography>
      ),
    },
    {
      field: 'updatedAt',
      width: 250,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.planChangeRequestsApprovedDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.updatedAt ? dateFormat(row.updatedAt) : '-'}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
        }}
      >
        <Buttons
          onClick={handleTransferOutClick}
          variant="contained"
          color="primary"
        >
          {strings.displayText.transferOut}
        </Buttons>
      </div>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}></Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={5} />
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.startDate}
            type="date"
            value={filters.startDate ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.endDate}
            type="date"
            value={filters.endDate ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={handleApplyFilter}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
      </Grid>

      <CustomDataGrid
        columns={columns}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows={transferData as any}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getRowId={(row: any) => `${String(row.requestId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        currentPage={commonConstantTexts.transferOutRequestPage as PageType}
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sortModel={checkSortingValues(pageInfo ?? { page: 0, pageSize: 10 }) as any}
        sortingMode={strings.displayText.sortingServer}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSortModelChange={((mode: Array<{ field: string; sort: string }>) => {
          dispatch(
            setSortBy({
              key: commonConstantTexts.transferOutRequestPage as PageType,
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: commonConstantTexts.transferOutRequestPage as PageType,
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: commonConstantTexts.transferOutRequestPage as PageType,
              value: 0,
            }),
          );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as any}
      />

      <DialogBox
        open={openDialog}
        onClose={handleDialogClose}
        title={strings.displayText.transferOut}
        fullWidth={true}
        className=""
      >
        <DialogContent style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            label={strings.displayText.enterEmailOrCustomer}
            variant="outlined"
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchValue(e.target.value);
            }}
            style={{ width: '400px' }}
          />
          <Buttons
            onClick={() => {
              void handleUserSearch(searchValue.trim());
            }}
            color="primary"
            style={{ marginLeft: '40px' }}
            disabled={searchValue === ''}
          >
            {strings.displayText.search}
          </Buttons>
        </DialogContent>

        <DialogContent>
          <>
            {searchedUserData?.uniqueId === zpUserUniqueId ? (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '20px',
                  }}
                >
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.firstName}
                      value={searchedUserData?.firstName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.lastName}
                      value={searchedUserData?.lastName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.email}
                      value={searchedUserData?.email}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.phoneNumber}
                      value={searchedUserData?.phone}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.customersId}
                      value={searchedUserData?.uniqueId}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6} />
                  {strings.displayText.currentHoldings}
                  <Grid item xs={6} />
                  <Grid item xs={6} />
                  {searchedUserData?.fundDetails?.map((item) => (
                    <>
                      <Grid item xs={6}>
                        <TextField
                          margin="normal"
                          label="Fund ID"
                          value={item?.fundId}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Tooltip title={item?.description} placement="top">
                          <TextField
                            margin="normal"
                            fullWidth
                            label="Fund Name"
                            value={item?.description}
                            InputProps={{ readOnly: true }}
                          />
                        </Tooltip>
                      </Grid>
                      <Grid item xs={10}>
                        <TextField
                          margin="normal"
                          label="Units"
                          value={item?.amount}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </>
                  ))}

                  {strings.displayText.enterHowMuchUnitstoTransferout}
                  <Grid item xs={6} />
                  <Grid item xs={6} />
                  {searchedUserData?.fundDetails?.map((item) => {
                    const amount = item?.amount;
                    return (
                      <>
                        <Grid item xs={6} key={item?.fundId}>
                          <TextField
                            margin="normal"
                            label="Fund ID"
                            value={item?.fundId}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            margin="normal"
                            fullWidth
                            label="Fund Name"
                            value={item?.description}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            margin="normal"
                            label="Units"
                            helperText={`Max: ${String(amount ?? 0)}`}
                            inputProps={{
                              min: 0,
                              max: amount,
                              type: 'number',
                            }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              handleInputChange(e, item?.fundId, amount);
                            }}
                          />
                        </Grid>
                      </>
                    );
                  })}
                </div>
              </>
            ) : (
              searchedUserData && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '20px',
                  }}
                >
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.firstName}
                      value={searchedUserData?.firstName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.lastName}
                      value={searchedUserData?.lastName}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.email}
                      value={searchedUserData?.email}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.phoneNumber}
                      value={searchedUserData?.phone}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="normal"
                      fullWidth
                      label={strings.displayText.uniqueId}
                      value={searchedUserData?.uniqueId}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  {searchedUserData?.fundDetails?.map((item) => (
                    <>
                      <Grid item xs={6}>
                        <Tooltip title={item?.description} placement="top">
                          <TextField
                            margin="normal"
                            fullWidth
                            label={item?.description}
                            value={item?.amount ?? '0'}
                            InputProps={{ readOnly: true }}
                          />
                        </Tooltip>
                      </Grid>
                    </>
                  ))}
                </div>
              )
            )}
          </>
        </DialogContent>
        <DialogActions>
          <Buttons
            disabled={
              Number(searchedUserData?.totalUnitsAllocated) === 0 ||
              searchValue === ''
            }
            onClick={() => {
              setOpenConfirmationDialog(true);
            }}
            color="primary"
          >
            {strings.displayText.transferOut}
          </Buttons>
        </DialogActions>
      </DialogBox>
      <AlertModal
        open={openConfirmationDialog}
        onClose={() => {
          setOpenConfirmationDialog(!openConfirmationDialog);
        }}
        title={strings.displayText.areYouConfirm}
        isButtonForConfirmation={true}
        handleConfirm={handleTransferOut}
      />
    </>
  );
};

export default TransferOutRequests;
